import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

// ── Page Layout ───────────────────────────────────────────────────────────────

export const DashboardContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  // padding: '24px 28px',
  // maxWidth: 1400,
  margin: '0 auto',
  width: '100%',
});

// ── Date Range Bar ────────────────────────────────────────────────────────────

export const DateRangeBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  borderBottom: `1px solid ${theme.palette.divider}`,
  paddingBottom: 16,
}));

// ── Stats Cards Row ───────────────────────────────────────────────────────────

export const StatsRow = styled(Box)({
  display: 'flex',
  gap: 16,
  flexWrap: 'wrap',
});

export const StatCard = styled(Box)(({ theme }) => ({
  flex: '1 1 100px',
  minWidth: 140,
  padding: '16px 20px',
  // background: theme.palette.secondary.extraLight,
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
}));

export const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
  marginBottom: 8,
  marginTop: 2,
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}));

export const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: 22,
  fontWeight: 600,
  lineHeight: 1.2,
  color: theme.palette.secondary.dark,
}));

export const StatSub = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.text.secondary,
  marginTop: 6,
}));

// ── Stats Bar cells ───────────────────────────────────────────────────────────

export const StatCell = styled(Box)<{ wide?: boolean }>(({ wide }) => ({
  flex: wide ? '1 1 140px' : '1 1 120px',
  paddingLeft: 16,
  paddingRight: 16,
  paddingTop: 10,
  paddingBottom: 10,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

export const StatDivider = styled('hr')(({ theme }) => ({
  alignSelf: 'center',
  height: 36,
  width: 1,
  border: 'none',
  backgroundColor: theme.palette.divider,
  margin: 0,
  flexShrink: 0,
}));

export const StatColorDot = styled(Box)<{ dotcolor?: string }>(({ dotcolor }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: dotcolor ?? '#9e9e9e',
  flexShrink: 0,
  marginTop: 3,
}));

export const StatTopRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 6,
});

// ── Content Row (chart + projects) ───────────────────────────────────────────

export const ContentRow = styled(Box)({
  display: 'flex',
  gap: 16,
  alignItems: 'flex-start',
  flexWrap: 'wrap',
});

// ── Chart Card ────────────────────────────────────────────────────────────────

export const ChartCard = styled(Box)(({ theme }) => ({
  flex: '1',
  minWidth: 320,
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  padding: '16px 20px',
}));

export const CardTitle = styled(Typography)({
  fontSize: 13,
  fontWeight: 600,
  marginBottom: 12,
});

export const CardTitleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 12,
});

export const ChartRelative = styled(Box)({
  position: 'relative',
});

export const ChartOverlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
});

export const ChartEmptyLabel = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.text.disabled,
}));

// ── Donut chart ───────────────────────────────────────────────────────────────

export const DonutCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 16,
  padding: 24,
  boxShadow: '0 1px 2px rgba(34,28,90,0.04), 0 8px 24px -8px rgba(34,28,90,0.06)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

export const DonutScrollInner = styled(Box)({
  height: '100%',
  overflowY: 'auto',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
});

export const DonutOuterRow = styled(Box)({
  display: 'flex',
  gap: 16,
  alignItems: 'stretch',
  height: 420,
});

export const DonutWrapper = styled(Box)({
  position: 'relative',
  width: 300,
  height: 300,
  flexShrink: 0,
});

export const DonutCenterOverlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
});

export const DonutCenterLabel = styled(Typography)({
  fontSize: 14,
  fontWeight: 700,
});

export const DonutSidePanel = styled(Box)(({ theme }) => ({
  flex: '1 1 0',
  minWidth: 0,
  minHeight: 0,
  overflow: 'hidden',
  background: theme.palette.background.paper,
  borderRadius: 16,
  boxShadow: '0 1px 2px rgba(34,28,90,0.04), 0 8px 24px -8px rgba(34,28,90,0.06)',
}));

// ── Top Projects table ────────────────────────────────────────────────────────

export const ProjectTableTitle = styled(Typography)({
  fontSize: 14,
  fontWeight: 700,
  padding: '16px 20px 12px',
});

export const ProjectTableHeader = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 160px 110px 52px 140px',
  gap: 8,
  padding: '10px 20px',
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : theme.palette.secondary.light,
}));

export const ProjectTableHeaderCell = styled(Typography)(({ theme }) => ({
  fontSize: 10,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
}));

export const ProjectTableRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 160px 110px 52px 140px',
  gap: 8,
  padding: '14px 20px',
  alignItems: 'center',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': { borderBottom: 'none' },
}));

export const ProjectTableNameCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minWidth: 0,
});

export const ProjectTableName = styled(Typography)({
  fontSize: 12,
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const ProjectTableCell = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const ProjectTableDuration = styled(Typography)({
  fontSize: 12,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
});

export const ProjectTablePct = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
  textAlign: 'right',
  paddingRight: 8,
}));

export const DonutTooltipBox = styled(Box)({
  backgroundColor: '#fff',
  color: '#222',
  borderRadius: 4,
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  paddingLeft: 12,
  paddingRight: 12,
  paddingTop: 8,
  paddingBottom: 8,
  fontSize: 12,
  minWidth: 160,
});

export const DonutTooltipRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginBottom: 4,
});

export const DonutTooltipDot = styled(Box)<{ dotcolor?: string }>(({ dotcolor }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: dotcolor ?? '#9e9e9e',
  flexShrink: 0,
}));

export const DonutTooltipName = styled(Typography)({
  fontSize: 12,
  color: '#444',
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const DonutTooltipValue = styled(Typography)({
  fontSize: 12,
  color: '#222',
  fontWeight: 600,
});

// ── Projects Table ────────────────────────────────────────────────────────────

export const ProjectsCard = styled(Box)(({ theme }) => ({
  flex: '0 0 300px',
  minWidth: 260,
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  padding: '16px 20px',
}));

export const ProjectRow = styled(Box)({
  paddingTop: 8,
  paddingBottom: 8,
});

export const ProjectColorDot = styled(Box)<{ dotcolor?: string }>(({ dotcolor }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: dotcolor ?? '#9e9e9e',
  flexShrink: 0,
}));

export const ProjectName = styled(Typography)({
  fontSize: 13,
  // flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const ProjectDuration = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
  flexShrink: 0,
  fontVariantNumeric: 'tabular-nums',
}));

export const ProjectPercent = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.text.disabled,
  flexShrink: 0,
  minWidth: 36,
  textAlign: 'right',
}));

// ── Project list layout ───────────────────────────────────────────────────────

export const ProjectListColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

export const ProjectListGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 40% 52px',
  alignItems: 'center',
  gap: 12,
});

export const ProjectInfoBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 6,
  minWidth: 0,
});

export const ProjectClientDuration = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.text.secondary,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
}));

export const ProjectLoadMoreRow = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  paddingTop: 12,
});

export const ProjectEmptyBox = styled(Box)(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.text.secondary,
  paddingTop: 16,
  paddingBottom: 16,
  textAlign: 'center',
}));

// ── Progress bar ──────────────────────────────────────────────────────────────

export const ProgressBarTrack = styled(Box)(({ theme }) => ({
  height: 4,
  borderRadius: 2,
  background: theme.palette.divider,
  marginTop: 4,
  overflow: 'hidden',
}));

export const ProgressBarFill = styled(Box)<{ fillcolor?: string; pct?: number }>(
  ({ fillcolor, pct }) => ({
    height: '100%',
    borderRadius: 2,
    backgroundColor: fillcolor ?? '#4caf50',
    width: `${pct ?? 0}%`,
    transition: 'width 0.4s ease',
  }),
);

// ── Team Activity Table ───────────────────────────────────────────────────────

export const TeamCardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  paddingBottom: 16,
  marginBottom: 4,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const TeamCardTitleBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
});

export const TeamCardTitle = styled(Typography)({
  fontSize: 14,
  fontWeight: 700,
  lineHeight: 1.3,
});

export const TeamCardSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
}));

export const TeamCardBadge = styled(Box)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
  backgroundColor: theme.palette.secondary.light,
  borderRadius: 20,
  padding: '3px 10px',
}));

export const TeamCard = styled(Box)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: 16,
  boxShadow: '0 1px 2px rgba(34,28,90,0.04), 0 8px 24px -8px rgba(34,28,90,0.06)',
  padding: '16px 20px',
}));

export const TeamTableHeader = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1.6fr 2fr 3fr',
  gap: 8,
  paddingTop: 12,
  paddingBottom: 12,
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginBottom: 4,
}));

export const TeamHeaderCell = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  userSelect: 'none',
  display: 'flex',
  alignItems: 'center',
}));

export const TeamHeaderGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

export const TeamRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1.6fr 2fr 3fr',
  gap: 8,
  alignItems: 'center',
  paddingTop: 16,
  paddingBottom: 16,
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': { borderBottom: 'none' },
}));

export const TeamMemberCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minWidth: 0,
});

export const TeamMemberNameWrap = styled(Box)({
  minWidth: 0,
});

export const TeamMemberAvatar = styled(Avatar)<{ bgcolor?: string }>(({ bgcolor }) => ({
  width: 36,
  height: 36,
  fontSize: 12,
  fontWeight: 700,
  flexShrink: 0,
  backgroundColor: bgcolor,
}));

export const TeamActivitySubText = styled(Typography)(({ theme }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
  fontSize: 11,
  color: theme.palette.text.secondary,
}));

export const TeamActivityTimeText = styled(Typography)<{ running?: boolean }>(
  ({ theme, running }) => ({
    flexShrink: 0,
    whiteSpace: 'nowrap',
    fontSize: 11,
    color: running ? theme.palette.success.main : theme.palette.text.secondary,
  }),
);

export const TeamMemberName = styled(Typography)({
  fontSize: 13,
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const TeamMemberSub = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.text.secondary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const TeamCell = styled(Typography)({
  fontSize: 13,
  fontVariantNumeric: 'tabular-nums',
});

export const TeamCellSub = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.text.secondary,
}));

// ── Team activity column cells ────────────────────────────────────────────────

export const TeamActivityCell = styled(Box)({
  minWidth: 0,
});

export const TeamActivityDescription = styled(Typography)({
  fontSize: 13,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const TeamActivitySubRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  marginTop: 2,
  overflow: 'hidden',
});

export const TeamActivityColorDot = styled(Box)<{ dotcolor?: string }>(({ dotcolor }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: dotcolor ?? '#9e9e9e',
  flexShrink: 0,
}));

export const TeamEmptyRow = styled(Box)(({ theme }) => ({
  paddingTop: 24,
  paddingBottom: 24,
  textAlign: 'center',
  color: theme.palette.text.secondary,
  fontSize: 13,
}));

// ── Team time + bar cell ──────────────────────────────────────────────────────

export const TeamTimeCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
});

export const TeamTimeCellValue = styled(Typography)({
  fontSize: 13,
  fontVariantNumeric: 'tabular-nums',
  width: 80,
  flexShrink: 0,
  whiteSpace: 'nowrap',
});

export const TeamBarProjectCount = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.text.disabled,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  width: 60,
  textAlign: 'right',
}));

// ── Sort icon ─────────────────────────────────────────────────────────────────

export const SortIconSpan = styled('span')<{ active?: boolean }>(({ active }) => ({
  marginLeft: 4,
  opacity: active ? 1 : 0.35,
  fontSize: 10,
}));

// ── Tooltip (team bar hover) ──────────────────────────────────────────────────

export const teamBarTooltipSx = {
  bgcolor: '#fff',
  color: '#222',
  boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
  p: 1.5,
  maxWidth: 320,
} as const;

export const teamBarTooltipArrowSx = { color: '#fff' } as const;

export const TeamTooltipBox = styled(Box)({
  minWidth: 200,
  padding: 4,
});

export const TeamTooltipTitle = styled(Typography)({
  fontSize: 12,
  fontWeight: 700,
  color: '#222',
  marginBottom: 6,
});

export const TeamTooltipRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginBottom: 3,
});

export const TeamTooltipDot = styled(Box)<{ dotcolor?: string }>(({ dotcolor }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: dotcolor ?? '#9e9e9e',
  flexShrink: 0,
}));

export const TeamTooltipName = styled(Typography)({
  fontSize: 11,
  flex: 1,
  color: '#444',
});

export const TeamTooltipDuration = styled(Typography)({
  fontSize: 11,
  color: '#555',
  marginLeft: 8,
  flexShrink: 0,
});

export const TeamTooltipPct = styled(Typography)({
  fontSize: 11,
  color: '#888',
  marginLeft: 4,
  flexShrink: 0,
  minWidth: 40,
  textAlign: 'right',
});

// ── Project mini bars (team activity) ────────────────────────────────────────

export const MiniBarTrack = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  height: 12,
  borderRadius: 10,
  background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
  overflow: 'hidden',
  width: '100%',
}));

export const TeamMiniBarTrack = styled(MiniBarTrack)({
  cursor: 'default',
  flex: 1,
});

export const MiniBarStack = styled(Box)({
  display: 'flex',
  gap: 0,
  alignItems: 'center',
  height: '100%',
  width: '100%',
  borderRadius: 10,
  overflow: 'hidden',
});

export const MiniBar = styled(Box)<{ barcolor?: string; widthpct?: number }>(
  ({ barcolor, widthpct }) => ({
    height: '100%',
    backgroundColor: barcolor ?? '#9e9e9e',
    width: `${widthpct ?? 0}%`,
    minWidth: widthpct ? 3 : 0,
    transition: 'width 0.3s ease',
    flexShrink: 0,
  }),
);

// ── Dashboard page toolbar (legacy — kept for backward compat) ────────────────

export const PageHeaderWrapper = styled(Box)({
  marginBottom: -24,
});

export const ToolbarRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  paddingTop: 0,
});

export const FilterLabelRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

export const FilterLabel = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.text.secondary,
  fontWeight: 500,
}));

export const LoadingBox = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  paddingTop: 48,
  paddingBottom: 48,
});

// ── Hero banner ───────────────────────────────────────────────────────────────

export const DashboardHeroRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 16,
  flexWrap: 'wrap',
  marginBottom: 20,
});

export const DashboardHeroTextBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

export const DashboardHeroTitle = styled(Typography)({
  fontSize: 22,
  fontWeight: 700,
  color: '#fff',
  lineHeight: 1.2,
});

export const DashboardHeroSubtitle = styled(Typography)({
  fontSize: 13,
  color: 'rgba(255,255,255,0.75)',
});

export const DashboardHeroActions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
});

export const DashboardHeroExportButton = styled(Button)({
  textTransform: 'none',
  fontSize: 13,
  fontWeight: 500,
  borderRadius: '8px',
  backgroundColor: '#fff',
  color: '#000000',
  border: '1.5px solid #e0daf7',
  gap: 6,
  padding: '6px 14px',
  '&:hover': { backgroundColor: '#f3f0ff', borderColor: '#c4b8f5' },
});

export const DashboardScopeToggle = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: 'rgba(255,255,255,0.15)',
  borderRadius: 8,
  padding: 3,
  gap: 2,
});

export const DashboardScopeButton = styled(Button)<{ active?: boolean }>(({ active }) => ({
  textTransform: 'none',
  fontSize: 13,
  fontWeight: active ? 600 : 400,
  color: active ? '#fff' : 'rgba(255,255,255,0.7)',
  backgroundColor: active ? 'rgba(255,255,255,0.20)' : 'transparent',
  borderRadius: 6,
  minWidth: 0,
  padding: '3px 14px',
  height: 28,
  '&:hover': {
    backgroundColor: active ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.10)',
  },
}));

export const DashboardHeroStatValueRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
});

export const DashboardHeroStatIcon = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  opacity: 0.8,
  flexShrink: 0,
});

export const DashboardHeroStatCards = styled(Box)({
  display: 'flex',
  gap: 12,
  flexWrap: 'wrap',
  width: '100%',
});

export const DashboardHeroStatCard = styled(Box)({
  flex: '1 1 140px',
  backgroundColor: 'rgba(255,255,255,0.10)',
  borderRadius: 12,
  padding: '14px 18px',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

export const DashboardHeroStatLabel = styled(Typography)({
  fontSize: 11,
  fontWeight: 600,
  color: 'rgba(255,255,255,0.7)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
});

export const DashboardHeroStatValue = styled(Typography)({
  fontSize: 20,
  fontWeight: 700,
  color: '#fff',
  lineHeight: 1.2,
});

export const DashboardHeroStatSub = styled(Typography)({
  fontSize: 11,
  color: 'rgba(255,255,255,0.6)',
  marginTop: 2,
});
